import { useQuery } from "@tanstack/react-query";
import { matchRepository } from "@/config/dataSource";
import { isLikelyStillLive } from "@/modules/leagues/repositories/shared/matchPredictionsHelpers";

export interface LiveRankingBoost {
  /** Soma de pontos provisórios (partidas ao vivo) por uid — pra somar em cima de `pontos` (Geral). */
  totalByUid: Record<string, number>;
  /** Mesma soma, mas só das partidas ao vivo da rodada X — pra somar em cima de `pontosPorRodada[X]`. */
  byUidAndRound: Record<string, Record<number, number>>;
  /** Mesma soma, agrupada por mês ("AAAA-MM") da partida — pra somar em cima de `pontosPorMes[mes]`. */
  byUidAndMonth: Record<string, Record<string, number>>;
}

/**
 * Ranking em tempo real — SÓ enquanto a partida está genuinamente AO VIVO
 * (regra explícita do usuário): atualiza minuto a minuto durante o jogo,
 * mas TRAVA assim que a partida termina (por horário real de Brasília,
 * refletido no status "finished" vindo da API) — nada de continuar
 * "estimando" um jogo já encerrado só porque o cron (1x/dia) ainda não
 * passou. Depois que o jogo acaba, o Ranking volta a mostrar exatamente
 * `pontos` (o valor oficial), parado, até o cron validar de verdade.
 *
 * Importante: isto é só uma pré-visualização por cima de `pontos` — a
 * pontuação OFICIAL continua sendo gravada exclusivamente pelo cron
 * (fonte única de verdade). Usa a MESMA função de cálculo
 * (ScoringEngine, via `getParticipantPredictions`) — nunca uma regra
 * paralela.
 */
export function useLiveRankingBoost(leagueId: string | undefined) {
  return useQuery({
    queryKey: ["live-ranking-boost", leagueId ?? "unknown"],
    queryFn: async (): Promise<LiveRankingBoost> => {
      const matches = await matchRepository.getByRound(leagueId as string);
      // Mesmo critério compartilhado (isLikelyStillLive) usado na aba Ao
      // vivo e na revelação de palpites — nunca confiar só no status cru
      // da API (pode demorar a atualizar), mas também nunca sem teto de
      // tempo (bug real corrigido: partida de ontem presa em "scheduled"
      // não pode contar como "ao vivo" hoje e inflar o ranking).
      const liveMatches = matches.filter(isLikelyStillLive);

      const result: LiveRankingBoost = { totalByUid: {}, byUidAndRound: {}, byUidAndMonth: {} };
      if (liveMatches.length === 0) return result;

      const predictionsPerMatch = await Promise.all(
        liveMatches.map((m) => matchRepository.getParticipantPredictions(leagueId as string, m.id))
      );

      liveMatches.forEach((match, idx) => {
        const monthKey = new Date(match.kickoff).toISOString().slice(0, 7);
        const predictions = predictionsPerMatch[idx] ?? [];
        for (const pred of predictions) {
          if (pred.pointsEarned === null || pred.isOfficial) continue; // null = sem placar ainda; oficial = já está em `pontos`, não somar de novo
          const pts = pred.pointsEarned;
          result.totalByUid[pred.uid] = (result.totalByUid[pred.uid] ?? 0) + pts;

          const byRound = result.byUidAndRound[pred.uid] ?? {};
          byRound[match.round] = (byRound[match.round] ?? 0) + pts;
          result.byUidAndRound[pred.uid] = byRound;

          const byMonth = result.byUidAndMonth[pred.uid] ?? {};
          byMonth[monthKey] = (byMonth[monthKey] ?? 0) + pts;
          result.byUidAndMonth[pred.uid] = byMonth;
        }
      });

      return result;
    },
    enabled: Boolean(leagueId),
    // Ritmo do "durante o jogo" — some sozinho quando não há mais partida
    // "live" (o filtro acima já garante isso na próxima leitura).
    refetchInterval: 30_000,
  });
}
